import { SearchIcon } from '@panwds/icons';
import { useLocalStorageState } from '@panwds/react-ui';
import { classNames } from '@panwds/tailwindcss';
import { animated, useSpring } from '@react-spring/web';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { BottomNavV3 } from './BottomNavV3/BottomNavV3.js';
import { CollapseV3 } from './CollapseV3/CollapseV3.js';
import { FlyOutMenuV3 } from './FlyOutMenuV3/FlyOutMenuV3.js';
import { Logo } from './LogoV3/LogoV3.js';
import { InnerNavigationProviderV3 } from './NavigationContextV3/NavigationContextV3.js';
import { findSelectedItemsById, getAppContainerBoxShadow, getAppContainerClasses, getMainNavClasses, getNavBarClasses, getNavClasses, getNavigationCollapseDividerClasses, getNavigationRootClasses, getNavWrapperClasses, getSubMenuWrapperClasses, navigationV3Classes, validateUniqueId } from './navigationV3.logic.js';
import { NavItemV3 } from './NavItemV3/NavItemV3.js';
import { SearchNavItem } from './NavItemV3/SearchNavItem.js';
import { SearchBar } from './SearchBar/index.js';
import { SubMenuV3 } from './SubMenuV3/SubMenuV3.js';
import { SubNavListItemV3 } from './SubNavListItemV3/SubNavListItemV3.js';
import { TopNavV3 } from './TopNavV3/TopNavV3.js';
import { useNavigationResize } from './useNavigationResize.js';
import { useNavigationWidths } from './useNavigationWidths.js';
import { useScrollableCheck } from './useScrollableCheck.js';
// Constants for navigation auto-collapse width threshold
export const navAutoCollapseWidthV3 = 1024;
const subNavItemRenderer = (items) => {
    return items.map((item) => React.createElement(SubNavListItemV3, { key: item.id, item: item }));
};
const navItemRenderer = ({ items, isBottomNav = false }) => {
    return items?.map((item) => (React.createElement(NavItemV3, { key: item.id, dotValue: item?.dotValue ?? 0, hasChildren: !!item.children, isBottomNav: isBottomNav, ...item })));
};
export function NavigationV3({ className, dataMetrics, dataTestId, collapsedLogo, expandedLogo, logoUrl, onCollapseChange, navConfig, brandColorClass = navigationV3Classes.brandColor, leftNavRef, hasCollapseController = true, collapse, activeBottomNavId, defaultActiveTopNavId, searchOptions, onLogoClick, onNavigate, children }) {
    const [storedState, setStoredState] = useLocalStorageState('navigationState', {
        collapsed: false
    });
    const [state, setState] = useState({
        brandColorClass,
        ...findSelectedItemsById(navConfig?.topNav ?? [], defaultActiveTopNavId),
        collapsed: collapse !== undefined ? collapse : storedState.collapsed || window.innerWidth < navAutoCollapseWidthV3
    });
    const [shadowSize, setShadowSize] = useState(6);
    const intl = useIntl();
    const topNavScrollRef = useRef(null);
    const internalLeftNav = useRef(null);
    const finalLeftNavRef = leftNavRef || internalLeftNav;
    const { collapsed, leftNavCollapsed, subNavCollapsed, visitedNav, selectedNav } = state;
    useEffect(() => {
        validateUniqueId(navConfig?.topNav ?? []);
        validateUniqueId(navConfig?.bottomNav ?? []);
    }, [navConfig]);
    // Hook to handle navigation resize and collapse behavior
    useNavigationResize({ collapsed, handleCollapse });
    const isTopNavScrollable = useScrollableCheck(topNavScrollRef);
    const { widthMain, widthLeftNav, widthSubNav } = useNavigationWidths(collapsed, leftNavCollapsed, subNavCollapsed);
    useEffect(() => {
        onCollapseChange?.(collapsed);
        setStoredState({ collapsed });
    }, [collapsed]);
    useEffect(() => {
        if (!leftNavCollapsed) {
            setShadowSize(6);
        }
    }, [leftNavCollapsed]);
    useEffect(() => {
        if (collapse !== undefined) {
            setState((prev) => ({ ...prev, collapsed: collapse }));
        }
    }, [collapse]);
    const findSelectedNavItem = useCallback((navConfig, id) => navConfig?.topNav?.find((item) => item.id === id) ?? navConfig?.bottomNav?.find((item) => item.id === id), []);
    const { navItemChildren, navItemTitle, navItemEmptyState } = useMemo(() => {
        const activeNavId = visitedNav ?? selectedNav;
        if (!activeNavId) {
            return { navItemChildren: null, navItemTitle: '', navItemEmptyState: null };
        }
        const selectedNavItem = findSelectedNavItem(navConfig, activeNavId);
        return {
            navItemChildren: selectedNavItem?.children ?? null,
            navItemTitle: selectedNavItem?.title ?? null,
            navItemEmptyState: selectedNavItem?.emptyState ?? null
        };
    }, [navConfig, visitedNav, selectedNav, collapsed, findSelectedNavItem]);
    useEffect(() => {
        const activeNavId = visitedNav ?? selectedNav;
        if (!activeNavId) {
            setState((prev) => ({ ...prev, subNavCollapsed: true, leftNavCollapsed: collapsed }));
        }
        else {
            setState((prev) => ({
                ...prev,
                subNavCollapsed: prev.collapsed || (!navItemChildren && !prev.collapsed),
                leftNavCollapsed: prev.collapsed || (!!navItemChildren && !prev.collapsed)
            }));
        }
    }, [navItemChildren, collapsed, visitedNav, selectedNav]);
    const navStyles = {
        root: getNavigationRootClasses(className?.root),
        main: getMainNavClasses(),
        navWrapper: getNavWrapperClasses(className?.navWrapper),
        nav: getNavClasses(leftNavCollapsed, className?.nav),
        navBar: getNavBarClasses(),
        navDivider: getNavigationCollapseDividerClasses(leftNavCollapsed),
        subMenuWrapper: getSubMenuWrapperClasses(),
        appContainer: getAppContainerClasses(className?.content?.root),
        appContainerBoxShadow: getAppContainerBoxShadow()
    };
    const boxShadowSpring = useSpring({
        opacity: leftNavCollapsed && !subNavCollapsed ? 1 : 0,
        config: { tension: 300, friction: 20 },
        onRest: () => {
            if (leftNavCollapsed && !subNavCollapsed) {
                setShadowSize(16);
            }
            else {
                setShadowSize(6);
            }
        }
    });
    const renderSearchBar = ({ isExpanded, autoFocus, callback }) => {
        return (React.createElement("div", { "data-testid": "navbar-search", className: classNames('twds-mx-2 twds-mb-2', isExpanded && 'twds-h-9') },
            React.createElement(SearchBar, { options: searchOptions ?? [], onChange: (item) => {
                    const result = findSelectedItemsById([...(navConfig?.topNav || []), ...(navConfig?.bottomNav || [])], item.id);
                    setState((prev) => ({
                        ...prev,
                        ...result,
                        ...(!collapsed && result.selectedSubNav && { leftNavCollapsed: true, subNavCollapsed: false })
                    }));
                    callback?.();
                    if (onNavigate && item.id && result?.selectedNavUrl) {
                        onNavigate({
                            id: item.id,
                            title: item.label,
                            url: result.selectedSubNavUrl || result.selectedNavUrl
                        });
                    }
                }, addInputClassName: "twds-border-[#9291914D] twds-bg-[#00000029] twds-text-gray-100", addDropdownClassName: "twds-z-50 twds-bg-gray-1000", showClearIcon: true, autoFocus: autoFocus, showCategories: true })));
    };
    return (React.createElement(InnerNavigationProviderV3, { value: { ...state, setState, activeBottomNavId, navConfig, onNavigate } },
        React.createElement("div", { className: navStyles.root },
            React.createElement(animated.div, { "data-metrics": dataMetrics, "data-testid": dataTestId || 'navigation', style: { width: widthMain.width, position: 'relative', overflow: 'hidden' }, ref: finalLeftNavRef, className: navStyles.main },
                React.createElement(animated.div, { className: navStyles.navWrapper, style: {
                        width: widthLeftNav.width,
                        boxShadow: boxShadowSpring.opacity.to((opacity) => `0px 2px ${shadowSize}px 0px rgba(20, 26, 31, ${opacity * 0.88})`)
                    } },
                    React.createElement("nav", { className: navStyles.nav, "aria-label": intl.formatMessage({
                            defaultMessage: 'Main Navigation',
                            id: 'Ekp0pa',
                            description: 'Label for navigation'
                        }) },
                        React.createElement(Logo, { collapsedLogo: collapsedLogo, expandedLogo: expandedLogo, className: className?.logo, logoUrl: logoUrl, onClick: onLogoClick }),
                        leftNavCollapsed ? (React.createElement(SearchNavItem, { icon: React.createElement(SearchIcon, null), title: "Search", id: "search-nav-item-id" })) : (renderSearchBar({ isExpanded: !collapsed })),
                        isTopNavScrollable && React.createElement("div", { className: navStyles.navDivider }),
                        React.createElement("div", { "data-testid": "navbar", className: navStyles.navBar },
                            React.createElement(TopNavV3, { scrollRef: topNavScrollRef, className: className?.topNav }, navItemRenderer({ items: navConfig?.topNav ?? [] })),
                            React.createElement("div", null,
                                navConfig?.bottomNav && isTopNavScrollable && React.createElement("div", { className: navStyles.navDivider }),
                                React.createElement(BottomNavV3, { className: className?.bottomNav }, navItemRenderer({
                                    items: navConfig?.bottomNav ?? [],
                                    isBottomNav: true
                                }))))),
                    hasCollapseController && (React.createElement(React.Fragment, null,
                        React.createElement("div", { className: navStyles.navDivider }),
                        React.createElement(CollapseV3, { changeCollapse: handleCollapse })))),
                collapsed && (React.createElement(FlyOutMenuV3, { title: state.openSearchFlyout ? 'Search' : navItemTitle, menuRef: state.flyOutRef, navRef: finalLeftNavRef, emptyState: navItemEmptyState, isOpen: !!state.flyoutId || state.openSearchFlyout, className: className?.flyout }, state.openSearchFlyout
                    ? renderSearchBar({
                        isExpanded: !collapsed,
                        autoFocus: true,
                        callback: () => {
                            setState((prev) => ({ ...prev, openSearchFlyout: false }));
                        }
                    })
                    : navItemChildren && subNavItemRenderer(navItemChildren))),
                React.createElement(animated.div, { style: {
                        width: widthSubNav.width,
                        display: widthSubNav.width.to((w) => (w === '0rem' ? 'none' : 'block'))
                    }, className: navStyles.subMenuWrapper },
                    React.createElement(SubMenuV3, { title: navItemTitle, emptyState: navItemEmptyState, className: className?.subMenu }, navItemChildren && subNavItemRenderer(navItemChildren)))),
            React.createElement("div", { style: { boxShadow: navStyles.appContainerBoxShadow }, className: navStyles.appContainer }, children))));
    function handleCollapse() {
        setState((prev) => ({
            ...prev,
            collapsed: !prev.collapsed,
            visitedNav: undefined,
            flyoutId: undefined,
            flyOutRef: undefined,
            openSearchFlyout: false
        }));
    }
}
//# sourceMappingURL=NavigationV3.js.map